import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

interface Position {
  position_id:     string;
  market_question: string;
  market_id:       string;
  side:            'YES' | 'NO';
  stake:           number;
  shares:          number;
  created_at:      string | null;
}

interface PortfolioData {
  balance:   number;
  positions: Position[];
}

const SIDE_STYLE: Record<string, { bg: string; text: string }> = {
  YES: { bg: '#14532d', text: '#86efac' },
  NO:  { bg: '#450a0a', text: '#fca5a5' },
};

interface Props { currentUser: string; }

export default function Portfolio({ currentUser }: Props): React.JSX.Element {
  const [data,  setData]  = useState<PortfolioData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cancelled = useRef(false);

  const fetchPortfolio = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/api/portfolio?user_id=${currentUser}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: PortfolioData = await res.json();
      if (!cancelled.current) { setData(json); setError(null); }
    } catch (e) {
      if (!cancelled.current) setError((e as Error).message);
    }
  }, [currentUser]);

  useEffect(() => {
    cancelled.current = false;
    fetchPortfolio();

    const socket = io(API_BASE);
    socket.on('market_update',   () => { if (!cancelled.current) fetchPortfolio(); });
    socket.on('market_resolved', (payload: { new_balance: number }) => {
      if (!cancelled.current) fetchPortfolio();
      if (!cancelled.current && payload.new_balance !== undefined)
        setData(prev => prev ? { ...prev, balance: payload.new_balance } : null);
    });
    socket.on('portfolio_reset', () => {
      if (!cancelled.current) fetchPortfolio();
    });

    return () => { cancelled.current = true; socket.disconnect(); };
  }, [fetchPortfolio]);

  return (
    <div
      className="w-full"
      style={{
        display:      'flex',
        flexDirection: 'row',
        alignItems:   'stretch',
        background:   '#0f172a',
        border:       '1px solid #1e293b',
        borderRadius: 6,
        fontFamily:   'monospace',
        overflow:     'hidden',
        minHeight:    44,
      }}
    >
      {/* ── Balance pill ─────────────────────────────────────────────────── */}
      <div style={{
        flexShrink:     0,
        display:        'flex',
        flexDirection:  'column',
        justifyContent: 'center',
        padding:        '6px 14px',
        borderRight:    '1px solid #1e293b',
        background:     '#1e293b',
        minWidth:       130,
      }}>
        <span style={{ color: '#64748b', fontSize: 7, letterSpacing: '0.18em', display: 'block' }}>
          AVAILABLE CAPITAL
        </span>
        <span style={{
          color:      error ? 'rgba(239,68,68,0.6)' : data ? '#7dd3fc' : '#334155',
          fontSize:   16,
          fontWeight: 700,
          letterSpacing: '0.03em',
          lineHeight: 1.2,
        }}>
          {error ? 'ERR' : data ? `$${data.balance.toFixed(2)}` : '—'}
        </span>
      </div>

      {/* ── Positions row ────────────────────────────────────────────────── */}
      <div style={{
        flex:           1,
        display:        'flex',
        flexDirection:  'column',
        justifyContent: 'center',
        padding:        '6px 10px',
        minWidth:       0,  /* allow flex child to shrink below content width */
      }}>
        <span style={{ color: '#64748b', fontSize: 7, letterSpacing: '0.18em', marginBottom: 4, display: 'block' }}>
          ACTIVE POSITIONS
        </span>

        {(!data || data.positions.length === 0) ? (
          <span style={{ color: '#334155', fontSize: 10 }}>No open positions.</span>
        ) : (
          <div style={{
            display:        'flex',
            gap:            5,
            overflowX:      'auto',
            overflowY:      'hidden',
            paddingBottom:  2,
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          } as React.CSSProperties}>
            {data.positions.map(p => {
              const s = SIDE_STYLE[p.side] ?? SIDE_STYLE.YES;
              return (
                <div
                  key={p.position_id}
                  title={`${p.market_question} — $${p.stake.toFixed(2)} stake`}
                  style={{
                    display:      'flex',
                    alignItems:   'center',
                    gap:          4,
                    flexShrink:   0,
                    background:   '#0f172a',
                    border:       '1px solid #334155',
                    borderRadius: 3,
                    padding:      '2px 7px',
                    fontSize:     10,
                    whiteSpace:   'nowrap',
                  }}
                >
                  <span style={{
                    fontSize: 7, fontWeight: 700, letterSpacing: '0.1em',
                    padding: '1px 4px', borderRadius: 2,
                    background: s.bg, color: s.text,
                  }}>
                    {p.side}
                  </span>
                  <span style={{ color: '#94a3b8', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.market_question}
                  </span>
                  <span style={{ color: '#e2e8f0', marginLeft: 2 }}>
                    ${p.stake.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
